import React, {useEffect, useMemo} from 'react';
import {
    StyledDigitizationCard,
    StyledDigitizationContent,
    StyledDigitizationPage
} from "./DocumentsDigitization.styled";
import DocumentsDigitizationHeader from "./components/DocumentsDigitizationHeader";
import {useDocumentsDigitization} from "../../../hooks/useDocumentDigitization";
import ResponsiveTable, {ResponsiveTableColumn} from "../../../components/common/ResponsiveTable";
import {Checkbox, CircularProgress, IconButton, Tooltip} from "@mui/material";
import {formatDateWithLanguageToOnlyDate} from "../../../utils/format-date";
import i18n from "i18next";
import {LocalDocumentCategory, LocalDocumentContentResponse} from "../../../models/local-documents.models";
import {useTranslation} from "react-i18next";
import DigitizedDocumentFileInfo from "../../../components/documents-digitization/DigitizedDocumentFileInfo";
import DigitizedDocumentStatus from "../../../components/documents-digitization/DigitizedDocumentStatus";
import DigitizedDocumentType from "../../../components/documents-digitization/DigitizedDocumentType";
import DocumentsDigitizationFooter from "./components/DocumentsDigitizationFooter";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {useSnackbar} from "../../../contexts/SnackbarContext";

const DocumentsDigitization = () => {
    const {t} = useTranslation();
    const {showSnackbar} = useSnackbar();

    const {
        data,
        isLoading,
        error,
        filters,
        page,
        pageSize,
        sortColumn,
        sortOrder,
        onFiltersChange,
        onSearchChange,
        onSortChange,
        onPageChange,
        onPageSizeChange,
        selectedDocumentIds,
        isAllOnPageSelected,
        isSomeOnPageSelected,
        onCheckboxToggleSelect,
        onCheckboxSelectAll,
        onCheckboxDeselectAll,
        navigateToDocument,
        exportFiles,
    } = useDocumentsDigitization();

    const locale = i18n.language;

    const columns: ResponsiveTableColumn<LocalDocumentContentResponse>[] = useMemo(() => [
        {
            id: 'checkbox',
            header: (
                <Checkbox
                    checked={isAllOnPageSelected}
                    indeterminate={isSomeOnPageSelected}
                    onChange={() => isAllOnPageSelected ? onCheckboxDeselectAll() : onCheckboxSelectAll()}
                    aria-label={t('documentsDigitization.table.checkAll') as string}
                />
            ),
            render: (doc) => (
                <Checkbox
                    checked={selectedDocumentIds.has(doc.localDocumentId)}
                    onChange={() => onCheckboxToggleSelect(doc.localDocumentId)}
                    aria-label={t('documentsDigitization.table.check', {fileName: doc.fileName}) as string}
                />
            ),
        },
        {
            id: 'fileName',
            header: t('documentsDigitization.table.fileName'),
            render: (doc) => <DigitizedDocumentFileInfo document={doc} />,
            sortable: true,
            sortKey: 'fileName',
        },
        {
            id: 'date',
            header: t('documentsDigitization.table.date'),
            render: (doc) => formatDateWithLanguageToOnlyDate(doc.createdAt, locale),
            sortable: true,
            sortKey: 'createdAt',
        },
        {
            id: 'status',
            header: t('documentsDigitization.table.status'),
            render: (doc) => <DigitizedDocumentStatus status={doc.status} />
        },
        {
            id: 'confidence',
            header: t('documentsDigitization.table.confidence'),
            render: (doc) => `${doc.confidencePercent}%`
        },
        {
            id: 'type',
            header: t('documentsDigitization.table.type'),
            render: (doc) => <DigitizedDocumentType type={doc.type as LocalDocumentCategory} />,
            sortable: true,
            sortKey: 'type',
        },
        {
            id: 'preview',
            header: '',
            render: (doc) => (
                <Tooltip describeChild title={t('documentsDigitization.table.preview')}>
                    <span>
                        <IconButton color="primary"
                            disabled={doc.status === 'ARCHIVED' || (!doc || !doc?.id)}
                            aria-disabled={doc.status === 'ARCHIVED' || (!doc || !doc?.id)}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigateToDocument(doc.id);
                            }}
                            aria-label={t('documentsDigitization.table.preview') as string}
                        >
                            <ChevronRightIcon sx={{ fontSize: 24 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            ),
        },
    ], [isAllOnPageSelected, isSomeOnPageSelected, locale, selectedDocumentIds, navigateToDocument, t]);

    useEffect(() => {
        if (error) {
            showSnackbar(t('documentsDigitization.messages.fetchError'), 'error');
        }
    }, [error, showSnackbar, t]);

    const handleExportFiles = async () => {
        const exportCompleted = await exportFiles();

        if (exportCompleted) {
            showSnackbar(t('documentsDigitization.export.exportSuccess'), 'success');
        } else {
            showSnackbar(t('documentsDigitization.export.exportError'), 'error');
        }
    };

    return (
        <StyledDigitizationPage>
            <StyledDigitizationCard>
                <DocumentsDigitizationHeader
                    filters={filters}
                    onFiltersChange={onFiltersChange}
                    onSearchChange={onSearchChange}
                    onExport={handleExportFiles}
                    isExportDisabled={selectedDocumentIds.size === 0}
                />

                <StyledDigitizationContent>
                    {isLoading
                        ? (
                            <CircularProgress />
                        ) : (
                            <ResponsiveTable
                                columns={columns}
                                data={data.content}
                                getRowKey={(d) => d.id}
                                emptyState={t('documentsDigitization.table.empty')}
                                ariaLabel={t('documentsDigitization.table.ariaLabel') as string}
                                sortColumn={sortColumn}
                                sortOrder={sortOrder}
                                onSortChange={onSortChange}
                                getRowClassName={(doc) => doc.status === 'ARCHIVED' ? 'cancelled' : undefined}
                            />
                        )}
                </StyledDigitizationContent>

                <DocumentsDigitizationFooter
                    page={page}
                    pageSize={pageSize}
                    totalPages={data.totalPages}
                    onPageChange={onPageChange}
                    onPageSizeChange={onPageSizeChange}
                />
            </StyledDigitizationCard>
        </StyledDigitizationPage>
    );
};

export default DocumentsDigitization;